import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useForm, type DefaultValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertCircle,
  Archive,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Search,
  Settings2,
  Ticket,
  TicketPercent,
  User as UserIcon,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SearchInput } from "@/components/ui/search-input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useDirtyFlag, useConfirmUnsaved } from "@/hooks/useUnsavedChanges";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useCreatePromoCode,
  useDeletePromoCode,
  usePromoCodeDetail,
  usePromoCodes,
  useUpdatePromoCode,
} from "@/hooks/useManagePromoCodes";
import { useUnsavedChangesStore } from "@/stores/unsavedChangesStore";
import { cn } from "@/lib/utils";
import { pingAPI } from "@/lib/ping-api";
import type {
  PromoCode,
  PromoCodeDiscountType,
  PromoCodePayload,
  PromoCodeStatus,
} from "@/types/promo-codes";
import type { User } from "@/types";
import {
  CopyButton,
  DetailRow,
  DetailSection,
  EmptyState,
  LoadingState,
} from "@/components/shared/detail-view";

type PromoCodeView = "list" | "add" | "view";

const TITLE_MAX = 100;
const DESCRIPTION_MAX = 144;

const requiredText = (label: string) =>
  z.string().trim().min(1, `${label} is required`);

const promoCodeSchema = z
  .object({
    code: requiredText("Code"),
    title: requiredText("Title").max(
      TITLE_MAX,
      `Title must be ${TITLE_MAX} characters or fewer`
    ),
    description: requiredText("Description").max(
      DESCRIPTION_MAX,
      `Description must be ${DESCRIPTION_MAX} characters or fewer`
    ),
    start_date: requiredText("Start date"),
    end_date: requiredText("End date"),
    usage_limit: z.string().optional(),
    discount_type: z.enum(["value", "percentage"], {
      required_error: "Select a discount type",
      invalid_type_error: "Select a discount type",
    }),
    discount_value: requiredText("Discount value").refine(
      (value) => Number(value) > 0,
      "Discount value must be greater than 0"
    ),
    redeemed_by_ids: z
      .array(
        z.string().uuid({
          message: "Assigned patient IDs must be valid UUIDs",
        })
      )
      .default([]),
    is_active: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.usage_limit && Number(values.usage_limit) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Usage limit must be greater than 0",
        path: ["usage_limit"],
      });
    }
    if (
      values.discount_type === "percentage" &&
      Number(values.discount_value) > 100
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Percentage discount cannot exceed 100",
        path: ["discount_value"],
      });
    }
    if (
      values.start_date &&
      values.end_date &&
      new Date(values.end_date) <= new Date(values.start_date)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date",
        path: ["end_date"],
      });
    }
  });

type PromoCodeFormValues = z.infer<typeof promoCodeSchema>;

const STATUS_LABEL: Record<PromoCodeStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  scheduled: "Scheduled",
  expired: "Expired",
  exhausted: "Exhausted",
  deleted: "Deleted",
};

const DISCOUNT_LABEL: Record<PromoCodeDiscountType, string> = {
  value: "Value",
  percentage: "Percentage",
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const formatDate = (value?: string | null) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(
    new Date(value)
  );
};

const usageTone = (promoCode: PromoCode) => {
  if (!promoCode.usage_limit) return "text-foreground";
  const ratio = promoCode.usage_count / promoCode.usage_limit;
  if (ratio >= 1) return "text-destructive";
  if (ratio >= 0.8) return "text-warning";
  return "text-foreground";
};

const toApiDateTime = (value: string) => new Date(value).toISOString();

const formatDiscount = (promoCode: PromoCode) =>
  promoCode.discount_type === "percentage"
    ? `${Number(promoCode.discount_value)}%`
    : `INR ${Number(promoCode.discount_value).toLocaleString("en-IN")}`;

const formatUsage = (promoCode: PromoCode) =>
  promoCode.usage_limit
    ? `${promoCode.usage_count} / ${promoCode.usage_limit}`
    : `${promoCode.usage_count} / Unlimited`;

const statusBadgeVariant = (status: PromoCodeStatus) => {
  if (status === "active") return "success";
  if (status === "scheduled") return "warning";
  if (status === "inactive" || status === "deleted") return "neutral";
  return "critical"; // expired, exhausted
};

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "scheduled", label: "Scheduled" },
  { value: "expired", label: "Expired" },
  { value: "exhausted", label: "Exhausted" },
  { value: "inactive", label: "Inactive" },
] as const;

const STATUS_TONE: Record<PromoCodeStatus, string> = {
  active: "bg-success/10 text-success",
  scheduled: "bg-warning/10 text-warning",
  expired: "bg-destructive/10 text-destructive",
  exhausted: "bg-destructive/10 text-destructive",
  inactive: "bg-muted text-muted-foreground",
  deleted: "bg-muted text-muted-foreground",
};

const getAccessibleUsers = (promoCode: PromoCode) => {
  if ((promoCode.redeemed_by_users ?? []).length > 0) {
    return promoCode.redeemed_by_users;
  }
  if (promoCode.redeemed_by_user_id && promoCode.redeemed_by_label) {
    return [
      {
        id: promoCode.redeemed_by_user_id,
        label: promoCode.redeemed_by_label,
      },
    ];
  }
  return [];
};

const formatAccessibleSummary = (promoCode: PromoCode) => {
  const users = getAccessibleUsers(promoCode);
  if (users.length === 0) return "Everyone";
  if (users.length === 1) return users[0].label;
  return `${users[0].label} +${users.length - 1}`;
};

// Start date is the only field that carries a default — everything else must
// be chosen explicitly by the user.
const buildDefaultValues = (): DefaultValues<PromoCodeFormValues> => ({
  code: "",
  title: "",
  description: "",
  start_date: new Date().toISOString(),
  end_date: "",
  usage_limit: "",
  discount_value: "",
  redeemed_by_ids: [],
  is_active: true,
  // discount_type intentionally omitted — no default
});

const buildPayload = (values: PromoCodeFormValues): PromoCodePayload => ({
  code: values.code.trim().toUpperCase(),
  title: values.title.trim(),
  description: values.description?.trim() ?? "",
  start_date: toApiDateTime(values.start_date),
  end_date: toApiDateTime(values.end_date),
  usage_limit: values.usage_limit ? Number(values.usage_limit) : null,
  discount_type: values.discount_type,
  discount_value: values.discount_value,
  redeemed_by_ids: values.redeemed_by_ids ?? [],
  is_active: values.is_active,
});

export const ManagePromoCodesHome = () => {
  const [view, setView] = useState<PromoCodeView>("list");
  const [selectedPromoCodeId, setSelectedPromoCodeId] = useState<number | null>(
    null
  );

  const goList = () => {
    setSelectedPromoCodeId(null);
    setView("list");
  };

  if (view === "add") {
    return <PromoCodeForm onBack={goList} />;
  }

  if (view === "view" && selectedPromoCodeId !== null) {
    return (
      <PromoCodeDetail
        promoCodeId={selectedPromoCodeId}
        onBack={goList}
        onDeleted={goList}
      />
    );
  }

  return (
    <PromoCodeList
      onAddNew={() => setView("add")}
      onView={(promoCodeId) => {
        setSelectedPromoCodeId(promoCodeId);
        setView("view");
      }}
    />
  );
};

const PromoCodeList = ({
  onAddNew,
  onView,
}: {
  onAddNew: () => void;
  onView: (promoCodeId: number) => void;
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebounce(searchInput, 350);
  const pageSize = 20;

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const {
    data: promoCodesData,
    isLoading,
    isError,
  } = usePromoCodes({
    page: currentPage,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
  });

  const promoCodes = promoCodesData?.results ?? [];
  const totalCount = promoCodesData?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const hasNext = !!promoCodesData?.next;
  const hasPrevious = !!promoCodesData?.previous;
  const hasActiveFilters = Boolean(searchInput) || Boolean(statusFilter);

  const clearFilters = () => {
    setSearchInput("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  return (
    <Card className="flex-1 min-h-0 flex flex-col border-none shadow-none">
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="z-h1 text-foreground">Promo Codes</CardTitle>
            <CardDescription className="text-muted-foreground">
              Create, review, and retire appointment discount codes from the
              console.
            </CardDescription>
          </div>
          <Button id="manage-promo-codes-add-btn" onClick={onAddNew}>
            <Plus className="mr-2 h-4 w-4" />
            New promo code
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 flex flex-col gap-4 p-0">
        {/* Status tabs */}
        <div className="px-6">
          <Tabs
            value={statusFilter || "all"}
            onValueChange={(next) =>
              setStatusFilter(next === "all" ? "" : next)
            }
          >
            <TabsList variant="line">
              {STATUS_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.value || "all"}
                  value={tab.value || "all"}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-6">
          <SearchInput
            placeholder="Search by code or title"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onClear={() => setSearchInput("")}
            containerClassName="w-full sm:w-80"
          />
          {searchInput && (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={clearFilters}
              className="z-caption h-auto p-0 text-muted-foreground hover:text-foreground"
            >
              Clear search
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 px-6 flex flex-col">
          {isLoading ? (
            <LoadingState label="Loading promo codes..." />
          ) : isError ? (
            <EmptyState
              icon={AlertCircle}
              title="Failed to load promo codes"
              description="Please try again in a moment."
            />
          ) : promoCodes.length === 0 ? (
            <EmptyState
              icon={Ticket}
              title="No promo codes found"
              description={
                hasActiveFilters
                  ? "Try adjusting your filters."
                  : "Create the first console-managed promo code."
              }
            />
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-border/60 bg-card shadow-sm">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow className="hover:bg-card">
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground pl-4">
                      Code
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                      Discount
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                      Validity
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                      Usage
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                      Accessible to
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                      Status
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground pr-4" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promoCodes.map((promoCode) => (
                    <TableRow
                      key={promoCode.id}
                      className="group bg-card transition-colors hover:bg-muted/50 cursor-pointer"
                      onClick={() => onView(promoCode.id)}
                    >
                      <TableCell className="pl-4">
                        <div className="min-w-0">
                          <p className="z-numeric font-mono text-foreground">
                            {promoCode.code}
                          </p>
                          <p className="z-caption truncate text-muted-foreground">
                            {promoCode.title}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="z-body text-foreground">
                        {formatDiscount(promoCode)}
                      </TableCell>
                      <TableCell className="z-caption text-muted-foreground whitespace-nowrap">
                        {formatDate(promoCode.start_date)}
                        <span className="px-1">→</span>
                        {formatDate(promoCode.end_date)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "z-numeric tabular-nums",
                          usageTone(promoCode)
                        )}
                      >
                        {formatUsage(promoCode)}
                      </TableCell>
                      <TableCell className="z-body text-muted-foreground">
                        {formatAccessibleSummary(promoCode)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(promoCode.status)}>
                          {STATUS_LABEL[promoCode.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="pr-4 text-right">
                        <Button
                          id={`view-promo-code-btn-${promoCode.id}`}
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto gap-0.5 p-0 text-primary"
                          onClick={(event) => {
                            event.stopPropagation();
                            onView(promoCode.id);
                          }}
                        >
                          Details
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Footer */}
        {totalCount > 0 ? (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <span className="z-secondary text-muted-foreground">
              Showing{" "}
              <span className="z-numeric font-medium text-foreground">
                {(currentPage - 1) * pageSize + 1}-
                {Math.min(currentPage * pageSize, totalCount)}
              </span>{" "}
              of{" "}
              <span className="z-numeric font-medium text-foreground">
                {totalCount}
              </span>{" "}
              promo codes
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPrevious || isLoading}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
                <span className="z-secondary text-muted-foreground px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNext || isLoading}
                  onClick={() => setCurrentPage((page) => page + 1)}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const PromoCodeDetail = ({
  promoCodeId,
  onBack,
  onDeleted,
}: {
  promoCodeId: number;
  onBack: () => void;
  onDeleted: () => void;
}) => {
  const { toast } = useToast();
  const [manageOpen, setManageOpen] = useState(false);
  const {
    data: promoCode,
    isLoading,
    isError,
  } = usePromoCodeDetail(promoCodeId);
  const deleteMutation = useDeletePromoCode();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !promoCode) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-20 text-muted-foreground">
        <Ticket className="mb-2 h-10 w-10 opacity-40" />
        <p className="z-body font-medium">Failed to load promo code details.</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(promoCode.id);
      toast({ title: "Promo code retired." });
      onDeleted();
    } catch {
      toast({
        title: "Retire failed",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    }
  };

  const status = promoCode.status;
  const tone = STATUS_TONE[status] ?? STATUS_TONE.inactive;
  const isRetired = status === "deleted";
  const accessibleUsers = getAccessibleUsers(promoCode);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-6">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-1">
        {/* Back + actions */}
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="-ml-2 gap-1 px-2 text-primary hover:bg-primary/5 hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={deleteMutation.isPending || isRetired}
                  className="text-destructive hover:text-destructive"
                >
                  <Archive className="mr-2 h-4 w-4" />
                  Retire
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Retire promo code?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This deactivates the code and removes it from console
                    management lists. It can no longer be redeemed. This cannot
                    be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Retire promo code
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              size="sm"
              onClick={() => setManageOpen(true)}
              disabled={isRetired}
            >
              <Settings2 className="mr-2 h-4 w-4" />
              Manage
            </Button>
          </div>
        </div>

        {/* Hero — the coupon, what it gives, and whether it's live */}
        <Card className="border border-border/60 shadow-sm">
          <CardContent className="flex items-center gap-4 px-6 py-5">
            <div
              className={cn(
                "flex h-14 w-14 shrink-0 items-center justify-center rounded-lg",
                tone
              )}
            >
              <TicketPercent className="h-7 w-7" strokeWidth={2} />
            </div>
            <div className="min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <p className="z-h1 text-foreground">
                  {formatDiscount(promoCode)} off
                </p>
                <Badge variant={statusBadgeVariant(status)}>
                  {STATUS_LABEL[status]}
                </Badge>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="z-body font-mono text-foreground">
                  {promoCode.code}
                </span>
                <CopyButton value={promoCode.code} />
              </div>
              {promoCode.description || promoCode.title ? (
                <p className="z-caption text-muted-foreground">
                  {promoCode.description || promoCode.title}
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Discount — the economic contract (immutable) */}
        <DetailSection title="Discount">
          <DetailRow
            label="Type"
            value={DISCOUNT_LABEL[promoCode.discount_type]}
          />
          <DetailRow label="Value" value={formatDiscount(promoCode)} />
        </DetailSection>

        {/* Validity & usage */}
        <DetailSection title="Validity & usage">
          <DetailRow
            label="Starts"
            value={formatDateTime(promoCode.start_date)}
          />
          <DetailRow label="Ends" value={formatDateTime(promoCode.end_date)} />
          <DetailRow
            label="Usage limit"
            value={promoCode.usage_limit ?? "Unlimited"}
          />
          <DetailRow label="Times used" value={promoCode.usage_count} />
        </DetailSection>

        {/* Accessible to — who may redeem it */}
        <DetailSection title="Accessible to">
          <DetailRow
            label="Patients"
            value={
              accessibleUsers.length === 0 ? (
                "Everyone"
              ) : (
                <div className="flex flex-wrap gap-2">
                  {accessibleUsers.map((user) => (
                    <Badge
                      key={user.id}
                      variant="secondary"
                      className="max-w-full justify-start"
                    >
                      <span className="truncate">{user.label}</span>
                    </Badge>
                  ))}
                </div>
              )
            }
          />
        </DetailSection>
      </div>

      <ManagePromoCodeDialog
        promoCode={promoCode}
        open={manageOpen}
        onOpenChange={setManageOpen}
      />
    </div>
  );
};

const DRAFT_KEY = "promo-code-draft:new";

const FIELD_LABEL = "z-label mb-1 text-muted-foreground";

const RequiredMark = () => <span className="text-destructive"> *</span>;

const FormSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Card className="border border-border/60 shadow-sm">
    <CardContent className="space-y-3 p-4">
      <p className="z-eyebrow uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {children}
    </CardContent>
  </Card>
);

const getPatientDisplayName = (patient: User) =>
  `${patient.first_name} ${patient.last_name}`.trim() ||
  patient.phone_number ||
  patient.id;

const getPatientInitials = (patient: User) =>
  `${patient.first_name.charAt(0)}${patient.last_name.charAt(0)}`
    .trim()
    .toUpperCase() || "P";

const getPatientMeta = (patient: User) =>
  [patient.phone_number, patient.zarity_user_id].filter(Boolean).join(" · ");

const PatientChipSelect = ({
  selectedPatients,
  onChange,
  placeholder = "Search patients",
}: {
  selectedPatients: User[];
  onChange: (patients: User[]) => void;
  placeholder?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [patients, setPatients] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(searchQuery, 300);

  const selectedIds = useMemo(
    () => new Set(selectedPatients.map((patient) => patient.id)),
    [selectedPatients]
  );

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    pingAPI.users
      .searchPatients(debouncedSearch)
      .then((result) => {
        if (!cancelled) setPatients(result || []);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load patients");
          setPatients([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, open]);

  const addPatient = (patient: User) => {
    if (selectedIds.has(patient.id)) return;
    onChange([...selectedPatients, patient]);
    setSearchQuery("");
  };

  const removePatient = (patientId: string) => {
    onChange(selectedPatients.filter((patient) => patient.id !== patientId));
  };

  return (
    <div className="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-lg border border-input bg-background px-3 py-2 text-sm">
      {selectedPatients.map((patient) => (
        <button
          key={patient.id}
          type="button"
          aria-label={`Remove ${getPatientDisplayName(patient)}`}
          className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => removePatient(patient.id)}
        >
          <span className="max-w-[12rem] truncate">
            {getPatientDisplayName(patient)}
          </span>
          <X className="h-3 w-3 text-muted-foreground" />
        </button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex min-h-6 min-w-[11rem] flex-1 items-center gap-2 rounded-md bg-transparent text-left text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {selectedPatients.length === 0 ? placeholder : "Add patient"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0">
          <div className="border-b border-border px-3 py-2.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by name, phone, or Zarity ID"
                className="h-auto border-0 bg-transparent px-0 py-0 pl-7 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-1.5">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin text-primary" />
                <span className="z-secondary">Searching...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8 text-destructive">
                <AlertCircle className="mr-2 h-4 w-4" />
                <span className="z-secondary">{error}</span>
              </div>
            ) : patients.length === 0 ? (
              <div className="py-8 text-center">
                <UserIcon className="mx-auto mb-2 h-7 w-7 text-muted-foreground/40" />
                <p className="z-secondary text-muted-foreground">
                  {searchQuery ? "No patients found" : "No patients available"}
                </p>
              </div>
            ) : (
              patients.map((patient) => {
                const isSelected = selectedIds.has(patient.id);
                return (
                  <button
                    key={patient.id}
                    type="button"
                    disabled={isSelected}
                    onClick={() => addPatient(patient)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isSelected && "cursor-not-allowed opacity-60"
                    )}
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                      {getPatientInitials(patient)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="z-body-md block truncate text-foreground">
                        {getPatientDisplayName(patient)}
                      </span>
                      <span className="z-caption block truncate text-muted-foreground/70">
                        {getPatientMeta(patient)}
                      </span>
                    </span>
                    {isSelected ? (
                      <Badge variant="neutral" className="shrink-0">
                        Added
                      </Badge>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

type UsageMode = "" | "unlimited" | "custom";
type AccessMode = "" | "everyone" | "specific";

const PromoCodeForm = ({ onBack }: { onBack: () => void }) => {
  const { toast } = useToast();
  const confirmUnsaved = useConfirmUnsaved();
  const clearDirty = useUnsavedChangesStore((s) => s.clearKey);
  const createMutation = useCreatePromoCode();
  const [selectedPatients, setSelectedPatients] = useState<User[]>([]);
  const [usageMode, setUsageMode] = useState<UsageMode>("");
  const [accessMode, setAccessMode] = useState<AccessMode>("");

  const form = useForm<PromoCodeFormValues>({
    resolver: zodResolver(promoCodeSchema),
    defaultValues: buildDefaultValues(),
    mode: "onChange",
  });

  const { isDirty } = form.formState;
  const dirtyKey = "manage-promo-code:new";
  useDirtyFlag(dirtyKey, isDirty, "Add Promo Code");

  const handlePatientsChange = (patients: User[]) => {
    setSelectedPatients(patients);
    form.setValue(
      "redeemed_by_ids",
      patients.map((patient) => patient.id),
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    );
  };

  const handleUsageMode = (mode: UsageMode) => {
    setUsageMode(mode);
    if (mode === "unlimited") {
      form.setValue("usage_limit", "", { shouldDirty: true });
      form.clearErrors("usage_limit");
    }
  };

  const handleAccessMode = (mode: AccessMode) => {
    setAccessMode(mode);
    if (mode === "everyone") {
      setSelectedPatients([]);
      form.setValue("redeemed_by_ids", [], { shouldDirty: true });
      form.clearErrors("redeemed_by_ids");
    }
  };

  const discountType = form.watch("discount_type");
  const isPercentage = discountType === "percentage";
  const valueLabel = isPercentage
    ? "Value (%)"
    : discountType === "value"
      ? "Value (₹)"
      : "Value";

  const clearDraft = () => {
    try {
      window.localStorage.removeItem(DRAFT_KEY);
    } catch {
      // storage unavailable; no-op
    }
  };

  // Restore an in-progress draft so a refresh doesn't lose entered data.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as {
        values?: Partial<PromoCodeFormValues> & { redeemed_by_id?: string };
        selectedPatient?: User | null;
        selectedPatients?: User[];
        usageMode?: UsageMode;
        accessMode?: AccessMode;
      };
      const draftPatients =
        draft.selectedPatients ??
        (draft.selectedPatient ? [draft.selectedPatient] : []);
      if (draft.values) {
        form.reset({
          ...buildDefaultValues(),
          ...draft.values,
          redeemed_by_ids:
            draft.values.redeemed_by_ids ??
            draftPatients.map((patient) => patient.id) ??
            [],
        } as PromoCodeFormValues);
      }
      if (draft.selectedPatients) {
        setSelectedPatients(draft.selectedPatients);
      } else if (draft.selectedPatient) {
        setSelectedPatients([draft.selectedPatient]);
      }
      if (draft.usageMode) setUsageMode(draft.usageMode);
      if (draft.accessMode) setAccessMode(draft.accessMode);
    } catch {
      // ignore malformed draft
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the draft as the user edits.
  const watched = form.watch();
  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({
            values: watched,
            selectedPatients,
            usageMode,
            accessMode,
          })
        );
      } catch {
        // storage unavailable; no-op
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [watched, selectedPatients, usageMode, accessMode]);

  const onSubmit = async (values: PromoCodeFormValues) => {
    if (usageMode === "") {
      form.setError("usage_limit", {
        message: "Choose Unlimited or set a custom limit",
      });
      return;
    }
    if (usageMode === "custom" && !values.usage_limit) {
      form.setError("usage_limit", {
        message: "Enter a usage limit or choose Unlimited",
      });
      return;
    }
    if (accessMode === "") {
      form.setError("redeemed_by_ids", {
        message: "Choose who this code is accessible to",
      });
      return;
    }
    if (accessMode === "specific" && values.redeemed_by_ids.length === 0) {
      form.setError("redeemed_by_ids", {
        message: "Select at least one patient or choose Everyone",
      });
      return;
    }

    try {
      await createMutation.mutateAsync(buildPayload(values));
      toast({ title: "Promo code created." });
      clearDirty(dirtyKey);
      clearDraft();
      onBack();
    } catch (error) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message)
          : "Please check your inputs and try again.";
      toast({
        title: "Something went wrong",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleCancel = () =>
    confirmUnsaved(() => {
      clearDraft();
      onBack();
    });

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-6">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-1">
        {/* Back + actions */}
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="-ml-2 gap-1 px-2 text-primary hover:bg-primary/5 hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              id="cancel-promo-code-btn"
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              id="save-promo-code-btn"
              type="submit"
              form="promo-code-form"
              size="sm"
              disabled={createMutation.isPending}
              className="min-w-28"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>

        {/* Title */}
        <div className="space-y-1">
          <h2 className="z-h2 text-foreground">New promo code</h2>
        </div>

        <Form {...form}>
          <form
            id="promo-code-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-3"
          >
            {/* Active — top */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-4 space-y-0 rounded-lg border border-border/60 bg-card px-4 py-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="z-label mb-0">Active</FormLabel>
                    <p className="z-caption text-muted-foreground">
                      {field.value
                        ? "Live as soon as it's saved"
                        : "Created paused — won't be redeemable"}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Details */}
            <FormSection title="Details">
              <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>
                        Code
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className="font-mono uppercase"
                          placeholder="WELCOME100"
                          onChange={(event) =>
                            field.onChange(event.target.value.toUpperCase())
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>
                        Title
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          maxLength={TITLE_MAX}
                          placeholder="Welcome offer"
                        />
                      </FormControl>
                      <div className="flex items-center justify-between gap-2">
                        <FormMessage />
                        <span className="z-hint ml-auto text-muted-foreground">
                          {field.value?.length ?? 0}/{TITLE_MAX}
                        </span>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className={FIELD_LABEL}>
                      Description
                      <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={2}
                        maxLength={DESCRIPTION_MAX}
                        placeholder="What this code is for"
                      />
                    </FormControl>
                    <div className="flex items-center justify-between gap-2">
                      <FormMessage />
                      <span className="z-hint ml-auto text-muted-foreground">
                        {field.value?.length ?? 0}/{DESCRIPTION_MAX}
                      </span>
                    </div>
                  </FormItem>
                )}
              />
            </FormSection>

            {/* Discount */}
            <FormSection title="Discount">
              <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="discount_type"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>
                        Type
                        <RequiredMark />
                      </FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="value">Value (INR)</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="discount_value"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>
                        {valueLabel}
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          step={isPercentage ? "1" : "0.01"}
                          placeholder={isPercentage ? "20" : "500"}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            {/* Validity & usage */}
            <FormSection title="Validity & usage">
              <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>
                        Start date
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <DateTimePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Pick start date & time"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>
                        End date
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <DateTimePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Pick end date & time"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="usage_limit"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>Usage limit</FormLabel>
                      <div className="flex items-center gap-2">
                        <Select
                          value={usageMode}
                          onValueChange={(value) =>
                            handleUsageMode(value as UsageMode)
                          }
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unlimited">Unlimited</SelectItem>
                            <SelectItem value="custom">Custom limit</SelectItem>
                          </SelectContent>
                        </Select>
                        {usageMode === "custom" ? (
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              min={1}
                              placeholder="e.g. 100"
                            />
                          </FormControl>
                        ) : null}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            {/* Accessible to */}
            <FormSection title="Accessible to">
              <FormField
                control={form.control}
                name="redeemed_by_ids"
                render={() => (
                  <FormItem className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Select
                        value={accessMode}
                        onValueChange={(value) =>
                          handleAccessMode(value as AccessMode)
                        }
                      >
                        <SelectTrigger className="w-44">
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="everyone">Everyone</SelectItem>
                          <SelectItem value="specific">
                            Specific patients
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {accessMode === "specific" ? (
                        <div className="min-w-[16rem] flex-1">
                          <PatientChipSelect
                            selectedPatients={selectedPatients}
                            onChange={handlePatientsChange}
                            placeholder="Search patient by name or Zarity ID"
                          />
                        </div>
                      ) : null}
                    </div>
                    <FormMessage />
                    <p className="z-hint text-muted-foreground">
                      {accessMode === "everyone"
                        ? "Any patient can redeem this code."
                        : accessMode === "specific"
                          ? "Only selected patients can redeem this code."
                          : "Choose whether anyone or specific patients can redeem this code."}
                    </p>
                  </FormItem>
                )}
              />
            </FormSection>
          </form>
        </Form>
      </div>
    </div>
  );
};

type ManageFormValues = {
  is_active: boolean;
  end_date: string;
  usage_limit?: string;
  title: string;
  description?: string;
};

const makeManageSchema = (promoCode: PromoCode) =>
  z
    .object({
      is_active: z.boolean(),
      end_date: requiredText("End date"),
      usage_limit: z.string().optional(),
      title: requiredText("Title").max(
        TITLE_MAX,
        `Title must be ${TITLE_MAX} characters or fewer`
      ),
      description: z
        .string()
        .max(
          DESCRIPTION_MAX,
          `Description must be ${DESCRIPTION_MAX} characters or fewer`
        )
        .optional(),
    })
    .superRefine((values, ctx) => {
      if (new Date(values.end_date) <= new Date(promoCode.start_date)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must be after the start date",
          path: ["end_date"],
        });
      }
      if (values.usage_limit) {
        const next = Number(values.usage_limit);
        if (!Number.isFinite(next) || next <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Usage limit must be greater than 0",
            path: ["usage_limit"],
          });
        } else if (next < promoCode.usage_count) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Cannot be below times already used (${promoCode.usage_count})`,
            path: ["usage_limit"],
          });
        }
      }
    });

const manageDefaults = (promoCode: PromoCode): ManageFormValues => ({
  is_active: promoCode.is_active,
  end_date: promoCode.end_date,
  usage_limit: promoCode.usage_limit ? String(promoCode.usage_limit) : "",
  title: promoCode.title,
  description: promoCode.description ?? "",
});

const ManagePromoCodeDialog = ({
  promoCode,
  open,
  onOpenChange,
}: {
  promoCode: PromoCode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { toast } = useToast();
  const updateMutation = useUpdatePromoCode();

  const form = useForm<ManageFormValues>({
    resolver: zodResolver(makeManageSchema(promoCode)),
    defaultValues: manageDefaults(promoCode),
    mode: "onChange",
  });

  useEffect(() => {
    if (open) form.reset(manageDefaults(promoCode));
  }, [open, promoCode, form]);

  const onSubmit = async (values: ManageFormValues) => {
    const payload: PromoCodePayload = {};
    if (values.is_active !== promoCode.is_active) {
      payload.is_active = values.is_active;
    }
    const nextEnd = toApiDateTime(values.end_date);
    if (
      new Date(nextEnd).getTime() !== new Date(promoCode.end_date).getTime()
    ) {
      payload.end_date = nextEnd;
    }
    const nextLimit = values.usage_limit ? Number(values.usage_limit) : null;
    if (nextLimit !== promoCode.usage_limit) {
      payload.usage_limit = nextLimit;
    }
    const nextTitle = values.title.trim();
    if (nextTitle !== promoCode.title) {
      payload.title = nextTitle;
    }
    const nextDesc = values.description?.trim() ?? "";
    if (nextDesc !== (promoCode.description ?? "")) {
      payload.description = nextDesc;
    }

    if (Object.keys(payload).length === 0) {
      onOpenChange(false);
      return;
    }

    try {
      await updateMutation.mutateAsync({ promoCodeId: promoCode.id, payload });
      toast({ title: "Promo code updated." });
      onOpenChange(false);
    } catch (error) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message)
          : "Please try again in a moment.";
      toast({
        title: "Update failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage promo code</DialogTitle>
          <DialogDescription>
            Code, discount, and eligibility are fixed once created. You can
            pause it, extend its window, raise the usage limit, or edit its
            label.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-4 py-3">
                  <div className="space-y-0.5">
                    <FormLabel className="z-label mb-0">Active</FormLabel>
                    <p className="z-caption text-muted-foreground">
                      {field.value
                        ? "Code can be redeemed"
                        : "Code is paused — cannot be redeemed"}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="z-label">End date</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Pick end date & time"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="usage_limit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="z-label">Usage limit</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={promoCode.usage_count || 1}
                      placeholder="Unlimited"
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="z-hint text-muted-foreground">
                    Leave blank for unlimited. {promoCode.usage_count} already
                    used.
                  </p>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="z-label">Title</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={TITLE_MAX} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="z-label">Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} maxLength={DESCRIPTION_MAX} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Save changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
